import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export interface RouteTabItem {
	/**
	 * The (relative) route segment this tab navigates to, e.g. `"profile"`.
	 * Doubles as the Radix tab `value` and is matched against the current URL
	 * to decide which tab is active. Must be unique within the group.
	 */
	route: string;
	/** Tab label. Treated as an i18n key unless `skipTranslation` is set. */
	label: string;
	/** Panel rendered while this tab is active. */
	content: React.ReactNode;
	/** Count pill after the label ("Kandidater ③"). Hidden when 0/undefined. */
	badge?: number;
	/** Omit the tab from the bar entirely (e.g. permission-gated). */
	hidden?: boolean;
	/** Render the trigger but make it non-interactive. */
	disabled?: boolean;
}

export interface RouteTabsProps {
	tabs: RouteTabItem[];
	/**
	 * Content rendered at the right edge of the tab bar — e.g. an actions menu.
	 * Mirrors the `actions` slot of the legacy `Shared/Tabs/TabGroup`.
	 */
	rightSlot?: React.ReactNode;
	/** Called right before navigating when a tab is selected. */
	onBeforeNavigate?: () => void;
	/** Skip i18n translation of the tab labels. */
	skipTranslation?: boolean;
	/** Outer container. */
	className?: string;
	/** The tab-bar row that holds the triggers and the `rightSlot` (carries the baseline border). */
	barClassName?: string;
	/** The tab bar (`TabsList`). */
	listClassName?: string;
	/** Each trigger. */
	triggerClassName?: string;
	/** Each panel (`TabsContent`). */
	contentClassName?: string;
}

/**
 * A route-aware tab bar built on the shadcn/Radix {@link Tabs} primitive — the
 * modern replacement for `Shared/Tabs/TabGroup` when navigating between the
 * sub-pages of a detail view (customer, consultant, job…). The active tab is
 * derived from the URL and selecting a tab navigates, so tabs stay
 * deep-linkable and browser back/forward keep working.
 *
 * This is intentionally NOT the pill control (`Shared/Tabs/Tab` with
 * `role="filter"`) — those pills remain the pattern for filters.
 *
 * @example
 * <RouteTabs
 *   tabs={[
 *     { route: routes.CUSTOMER_PROFILE, label: "PROFILE", content: <CustomerProfileTab ... /> },
 *     { route: routes.CUSTOMER_JOBS, label: "JOBS", content: <CustomerJobsTab ... /> },
 *   ]}
 *   rightSlot={<ActionsPopOver actions={customerActions} label="MORE" />}
 * />
 */
const RouteTabs: React.FC<RouteTabsProps> = ({
	tabs,
	rightSlot,
	onBeforeNavigate,
	skipTranslation = false,
	className,
	barClassName,
	listClassName,
	triggerClassName,
	contentClassName,
}) => {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const { t } = useTranslation();

	const visibleTabs = tabs.filter((tab) => !tab.hidden);

	// Match the active tab by looking for its route segment in the current path —
	// the same approach the legacy `Tab` used. Falls back to the first tab so the
	// bar always has an active state (e.g. on the bare parent route).
	const pathSegments = pathname.split("/");
	const activeRoute =
		visibleTabs.find((tab) => pathSegments.includes(tab.route))?.route ?? visibleTabs[0]?.route;

	const handleValueChange = (route: string) => {
		if (route === activeRoute) return;
		onBeforeNavigate?.();
		navigate(route);
	};

	const translate = (label: string) => (skipTranslation ? label : t(label as any));

	if (visibleTabs.length === 0) return null;

	return (
		<Tabs value={activeRoute} onValueChange={handleValueChange} className={cn("w-full", className)}>
			<div
				className={cn(
					"flex items-center justify-between gap-4 border-b border-gray-200 shadow-sm",
					barClassName
				)}
			>
				<TabsList
					className={cn(
						// Override the primitive's segmented look with an underline tab
						// bar — the standard page-navigation style.
						"h-auto justify-start gap-1 rounded-none bg-transparent p-0 text-gray-500",
						listClassName
					)}
				>
					{visibleTabs.map((tab) => (
						<TabsTrigger
							key={tab.route}
							value={tab.route}
							disabled={tab.disabled}
							className={cn(
								"relative rounded-none border-b-2 border-transparent px-4 py-2.5 font-semibold text-gray-500 shadow-none transition-colors hover:text-gray-700 data-[state=active]:border-action data-[state=active]:bg-transparent data-[state=active]:text-action data-[state=active]:shadow-none",
								triggerClassName
							)}
						>
							{translate(tab.label)}
							{!!tab.badge && (
								<span className="ml-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-action px-1 text-[11px] font-bold tabular-nums text-white">
									{tab.badge}
								</span>
							)}
						</TabsTrigger>
					))}
				</TabsList>

				{rightSlot}
			</div>

			{visibleTabs.map((tab) => (
				<TabsContent
					key={tab.route}
					value={tab.route}
					className={cn(
						"data-[state=active]:animate-in data-[state=active]:fade-in-0",
						contentClassName
					)}
				>
					{tab.content}
				</TabsContent>
			))}
		</Tabs>
	);
};

export default RouteTabs;
